import * as THREE from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { Viewport } from '../scene/viewport.ts';
import { state } from '../state.ts';
import { findCamera, type CameraDef } from './types.ts';
import { evalCamera, type CameraSample } from './evaluate.ts';

export interface CameraVizHandlers {
  onSelectCamera: (id: string) => void;
  onSelectKeyframe: (index: number) => void;
  /** A waypoint gizmo moved: write the new world position into the keyframe. */
  onMoveKeyframe: (cameraId: string, index: number, position: THREE.Vector3) => void;
  /** The aim-point marker was clicked (clears the keyframe selection). */
  onSelectAimPoint: () => void;
  /** The aim-point marker moved: write it into the camera's point aim. */
  onMoveAimPoint: (cameraId: string, position: THREE.Vector3) => void;
}

/**
 * In-viewport camera authoring viz: the selected camera's path line + waypoint
 * spheres (draggable via TransformControls), a dashed look-at path, and a small
 * tinted body + frustum for every camera. Visible ONLY in Free view and never
 * while exporting, so it can't leak into MP4 frames.
 */
export function createCameraViz(vp: Viewport, handlers: CameraVizHandlers) {
  const group = new THREE.Group();
  group.name = 'CameraViz';
  group.visible = false;
  vp.scene.add(group);

  const scratch: CameraSample = {
    position: new THREE.Vector3(),
    target: new THREE.Vector3(),
    fov: 45,
  };

  // --- Selected camera: path line, target line, waypoints -----------------------

  const pathGeometry = new THREE.BufferGeometry();
  const pathLine = new THREE.Line(
    pathGeometry,
    new THREE.LineBasicMaterial({ transparent: true, opacity: 0.9 })
  );
  pathLine.frustumCulled = false;
  group.add(pathLine);

  const targetGeometry = new THREE.BufferGeometry();
  const targetLine = new THREE.Line(
    targetGeometry,
    new THREE.LineDashedMaterial({ dashSize: 0.08, gapSize: 0.06, transparent: true, opacity: 0.4 })
  );
  targetLine.frustumCulled = false;
  group.add(targetLine);

  const waypointGroup = new THREE.Group();
  group.add(waypointGroup);
  const waypointGeometry = new THREE.SphereGeometry(0.055, 16, 12);
  let waypoints: THREE.Mesh[] = [];

  /** Draggable ⌖ marker for the 'fixed point' aim mode. */
  const aimMarker = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.09),
    new THREE.MeshBasicMaterial({ color: 0xffb347, depthTest: false, transparent: true, opacity: 0.95 })
  );
  aimMarker.renderOrder = 11;
  aimMarker.visible = false;
  group.add(aimMarker);

  // --- Ghost cameras (bodies + frustums) for every camera ------------------------

  const ghostGroup = new THREE.Group();
  group.add(ghostGroup);
  interface Ghost {
    def: CameraDef;
    cam: THREE.PerspectiveCamera;
    helper: THREE.CameraHelper;
    body: THREE.Mesh;
  }
  let ghosts: Ghost[] = [];
  const bodyGeometry = new THREE.BoxGeometry(0.16, 0.12, 0.22);

  // --- Waypoint dragging (TransformControls) ------------------------------------

  const tc = new TransformControls(vp.camera, vp.renderer.domElement);
  tc.setMode('translate');
  tc.setSize(0.7);
  const tcHelper = tc.getHelper();
  tcHelper.visible = false;
  vp.scene.add(tcHelper);
  let dragging = false;

  tc.addEventListener('dragging-changed', (e) => {
    dragging = e.value as boolean;
    // Don't let the orbit fight the gizmo (only relevant in Free view anyway).
    if (state.cameraView === 'free') vp.controls.enabled = !dragging;
    if (!dragging) {
      lastSignature = '';
      update();
    }
  });
  tc.addEventListener('objectChange', () => {
    const def = findCamera(state.cameras, state.selectedCameraId);
    if (!def || !tc.object) return;
    if (tc.object === aimMarker) {
      handlers.onMoveAimPoint(def.id, aimMarker.position);
      return;
    }
    const index = state.selectedKeyframe;
    if (index === null) return;
    handlers.onMoveKeyframe(def.id, index, tc.object.position);
    refreshPathLines(def); // live path update while dragging (no rebuild)
  });

  // --- Picking (click = select waypoint or camera body) ---------------------------

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let downAt: { x: number; y: number } | null = null;

  vp.renderer.domElement.addEventListener('pointerdown', (e) => {
    downAt = { x: e.clientX, y: e.clientY };
  });
  vp.renderer.domElement.addEventListener('pointerup', (e) => {
    if (!downAt || !group.visible || dragging) return;
    const moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
    downAt = null;
    if (moved > 4) return; // it was an orbit drag, not a click

    const rect = vp.renderer.domElement.getBoundingClientRect();
    pointer.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1)
    );
    raycaster.setFromCamera(pointer, vp.camera);

    if (aimMarker.visible && raycaster.intersectObject(aimMarker, false).length > 0) {
      handlers.onSelectAimPoint(); // clears the keyframe selection → gizmo returns to the marker
      return;
    }
    const wpHit = raycaster.intersectObjects(waypoints, false)[0];
    if (wpHit) {
      handlers.onSelectKeyframe(wpHit.object.userData.index as number);
      return;
    }
    const bodyHit = raycaster.intersectObjects(
      ghosts.map((g) => g.body),
      false
    )[0];
    if (bodyHit) handlers.onSelectCamera(bodyHit.object.userData.cameraId as string);
  });

  // --- Rebuild / refresh -----------------------------------------------------------

  let lastSignature = '';

  /** Sample a camera without its shake layer (paths should be smooth). */
  function calm(def: CameraDef): CameraDef {
    return def.shake ? { ...def, shake: null } : def;
  }

  function refreshPathLines(def: CameraDef): void {
    const keys = def.keyframes;
    const t0 = keys[0].time;
    const t1 = keys[keys.length - 1].time;
    const still = calm(def);
    const posPts: THREE.Vector3[] = [];
    const tgtPts: THREE.Vector3[] = [];
    const samples = keys.length === 1 ? 1 : 128;
    for (let i = 0; i <= samples; i++) {
      const t = t0 + ((t1 - t0) * i) / samples;
      evalCamera(still, t, undefined, scratch);
      posPts.push(scratch.position.clone());
      tgtPts.push(scratch.target.clone());
    }
    pathGeometry.setFromPoints(posPts);
    if (def.aim.mode === 'path') {
      targetGeometry.setFromPoints(tgtPts);
      targetLine.computeLineDistances();
      targetLine.visible = keys.length > 1;
    } else {
      targetLine.visible = false;
    }
    pathLine.visible = keys.length > 1;
  }

  function rebuild(): void {
    // Waypoints for the selected camera.
    for (const w of waypoints) waypointGroup.remove(w);
    for (const w of waypoints) (w.material as THREE.Material).dispose();
    waypoints = [];
    tc.detach();
    tcHelper.visible = false;

    const def = findCamera(state.cameras, state.selectedCameraId);
    if (def) {
      const color = new THREE.Color(def.color);
      (pathLine.material as THREE.LineBasicMaterial).color.copy(color);
      (targetLine.material as THREE.LineDashedMaterial).color.copy(color);
      refreshPathLines(def);

      // Fixed-point aim marker — the gizmo sits on it whenever no keyframe is
      // selected, so switching to "Fixed point" is immediately draggable.
      if (def.aim.mode === 'point') {
        aimMarker.visible = true;
        aimMarker.position.fromArray(def.aim.point);
        if (state.selectedKeyframe === null) {
          tc.attach(aimMarker);
          tcHelper.visible = group.visible;
        }
      } else {
        aimMarker.visible = false;
      }

      def.keyframes.forEach((k, index) => {
        const selected = index === state.selectedKeyframe;
        const mesh = new THREE.Mesh(
          waypointGeometry,
          new THREE.MeshBasicMaterial({
            color: selected ? 0xffffff : def.color,
            depthTest: false,
            transparent: true,
            opacity: selected ? 1 : 0.85,
          })
        );
        mesh.renderOrder = 10;
        mesh.position.fromArray(k.position);
        mesh.scale.setScalar(selected ? 1.35 : 1);
        mesh.userData.index = index;
        waypointGroup.add(mesh);
        waypoints.push(mesh);
        if (selected) {
          tc.attach(mesh);
          tcHelper.visible = group.visible;
        }
      });
    } else {
      pathLine.visible = false;
      targetLine.visible = false;
      aimMarker.visible = false;
    }

    // Ghost bodies + frustums for every camera.
    for (const g of ghosts) {
      ghostGroup.remove(g.helper, g.body);
      g.helper.dispose();
      (g.body.material as THREE.Material).dispose();
    }
    ghosts = state.cameras.cameras.map((cam) => {
      // A short far plane keeps the frustum helper a compact "lens cone".
      const ghost = new THREE.PerspectiveCamera(cam.keyframes[0].fov, vp.camera.aspect, 0.12, 0.6);
      const helper = new THREE.CameraHelper(ghost);
      const helperMat = helper.material as THREE.LineBasicMaterial;
      helperMat.color.setHex(cam.color);
      helperMat.transparent = true;
      helperMat.opacity = 0.7;
      const body = new THREE.Mesh(
        bodyGeometry,
        new THREE.MeshBasicMaterial({ color: cam.color, transparent: true, opacity: 0.55 })
      );
      body.userData.cameraId = cam.id;
      ghostGroup.add(helper, body);
      return { def: cam, cam: ghost, helper, body };
    });
  }

  /** Per-frame: pose the ghost cameras at the current playhead. */
  function tick(seconds: number): void {
    if (!group.visible) return;
    for (const g of ghosts) {
      evalCamera(calm(g.def), seconds, undefined, scratch);
      g.cam.position.copy(scratch.position);
      g.cam.lookAt(scratch.target);
      if (g.cam.fov !== scratch.fov) {
        g.cam.fov = scratch.fov;
        g.cam.updateProjectionMatrix();
      }
      g.cam.updateMatrixWorld();
      g.helper.update();
      g.body.position.copy(scratch.position);
      g.body.quaternion.copy(g.cam.quaternion);
    }
  }

  /** Reflect state (called on every notify()). */
  function update(): void {
    const editMode = state.cameraView === 'free' && !state.exporting;
    group.visible = editMode;
    tcHelper.visible = editMode && tc.object !== undefined && tc.object !== null;
    tc.enabled = editMode;
    if (!editMode) return;
    if (dragging) return; // keep DOM/scene stable mid-drag

    const def = findCamera(state.cameras, state.selectedCameraId);
    const signature = JSON.stringify([
      state.selectedCameraId,
      state.selectedKeyframe,
      def?.keyframes,
      def?.aim,
      state.cameras.cameras.map((c) => [c.id, c.color, c.keyframes.length]),
    ]);
    if (signature !== lastSignature) {
      lastSignature = signature;
      rebuild();
    }
  }

  return { update, tick };
}
